import {Three as THREE} from "@cm/material-nodes/three"

export type UVBounds = {
    minU: number
    minV: number
    maxU: number
    maxV: number
}

export const getMinMaxUVs = (uv: THREE.BufferAttribute) => {
    if (uv.itemSize !== 2) throw new Error("UV item size must be 2")
    if (uv.count === 0) return {minU: 0, minV: 0, maxU: 0, maxV: 0} as UVBounds
    let minU = Infinity
    let minV = Infinity
    let maxU = -Infinity
    let maxV = -Infinity
    for (let i = 0; i < uv.array.length; i += uv.itemSize) {
        const u = uv.array[i]
        const v = uv.array[i + 1]
        minU = Math.min(minU, u)
        minV = Math.min(minV, v)
        maxU = Math.max(maxU, u)
        maxV = Math.max(maxV, v)
    }
    return {minU, minV, maxU, maxV} as UVBounds
}

export const scaleUVs = (uv: THREE.BufferAttribute, scale: number) => {
    for (let i = 0; i < uv.array.length; i += uv.itemSize) {
        uv.array[i] *= scale
        uv.array[i + 1] *= scale
    }
}

export const ensureUVSideness = (
    uv: THREE.BufferAttribute,
    indexBuffer: THREE.BufferAttribute | null,
    side: typeof THREE.FrontSide | typeof THREE.BackSide,
) => {
    if (!indexBuffer) throw new Error("Index is missing")
    if (indexBuffer.array.length === 0) throw new Error("Index is empty")
    if (indexBuffer.array.length % 3 !== 0) throw new Error("Index is not a multiple of 3")

    let backFacing = false
    for (let i = 0; i < indexBuffer.array.length; i += 3) {
        const a = new THREE.Vector2().fromBufferAttribute(uv, indexBuffer.array[i])
        const b = new THREE.Vector2().fromBufferAttribute(uv, indexBuffer.array[i + 1])
        const c = new THREE.Vector2().fromBufferAttribute(uv, indexBuffer.array[i + 2])
        const det = a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y)

        if (det < 0.0) backFacing = true

        break
    }

    if (side === THREE.FrontSide && backFacing) return
    if (side === THREE.BackSide && !backFacing) return

    for (let i = 0; i < uv.array.length; i += uv.itemSize) uv.array[i] *= -1
}

export const getUVSurfaceArea = (uv: THREE.BufferAttribute, indexBuffer: THREE.BufferAttribute | null) => {
    if (!indexBuffer) throw new Error("Index is missing")
    if (indexBuffer.array.length === 0) throw new Error("Index is empty")
    if (indexBuffer.array.length % 3 !== 0) throw new Error("Index is not a multiple of 3")

    const getTriangleArea = (v0: THREE.Vector2, v1: THREE.Vector2, v2: THREE.Vector2) => {
        const a = v0.distanceTo(v1)
        const b = v1.distanceTo(v2)
        const c = v2.distanceTo(v0)
        const s = (a + b + c) / 2
        return Math.sqrt(s * (s - a) * (s - b) * (s - c))
    }

    let area = 0
    for (let i = 0; i < indexBuffer.array.length; i += 3) {
        const v0 = new THREE.Vector2().fromBufferAttribute(uv, indexBuffer.array[i])
        const v1 = new THREE.Vector2().fromBufferAttribute(uv, indexBuffer.array[i + 1])
        const v2 = new THREE.Vector2().fromBufferAttribute(uv, indexBuffer.array[i + 2])
        const faceArea = getTriangleArea(v0, v1, v2)
        if (faceArea <= 0 || !isFinite(faceArea)) continue
        area += faceArea
    }

    return area
}

export const getGeometrySurfaceArea = (positions: THREE.BufferAttribute | THREE.InterleavedBufferAttribute, indexBuffer: THREE.BufferAttribute | null) => {
    if (!indexBuffer) throw new Error("Index is missing")
    if (indexBuffer.array.length === 0) throw new Error("Index is empty")
    if (indexBuffer.array.length % 3 !== 0) throw new Error("Index is not a multiple of 3")

    const getTriangleArea = (v0: THREE.Vector3, v1: THREE.Vector3, v2: THREE.Vector3) => {
        const a = v0.distanceTo(v1)
        const b = v1.distanceTo(v2)
        const c = v2.distanceTo(v0)
        const s = (a + b + c) / 2
        return Math.sqrt(s * (s - a) * (s - b) * (s - c))
    }

    let area = 0
    for (let i = 0; i < indexBuffer.array.length; i += 3) {
        const v0 = new THREE.Vector3().fromBufferAttribute(positions, indexBuffer.array[i])
        const v1 = new THREE.Vector3().fromBufferAttribute(positions, indexBuffer.array[i + 1])
        const v2 = new THREE.Vector3().fromBufferAttribute(positions, indexBuffer.array[i + 2])
        const faceArea = getTriangleArea(v0, v1, v2)
        if (faceArea <= 0 || !isFinite(faceArea)) continue
        area += faceArea
    }

    return area
}

export const uvOverlappingRatio = (uv: THREE.BufferAttribute, indexBuffer: THREE.BufferAttribute | null, rasterResolution: number) => {
    if (!indexBuffer) throw new Error("Index is missing")
    if (indexBuffer.array.length === 0) throw new Error("Index is empty")
    if (indexBuffer.array.length % 3 !== 0) throw new Error("Index is not a multiple of 3")

    const {minU, minV, maxU, maxV} = getMinMaxUVs(uv)

    const uSize = maxU - minU
    const vSize = maxV - minV
    const uvToRaster = (1 / Math.max(uSize, vSize)) * rasterResolution

    const resX = Math.ceil(uSize * uvToRaster)
    const resY = Math.ceil(vSize * uvToRaster)

    const rasterizedAtlas = new Uint8Array(resX * resY)

    let totalPixels = 0
    let totalOverlappingPixels = 0
    for (let i = 0; i < indexBuffer.array.length; i += 3) {
        const v0 = new THREE.Vector2().fromBufferAttribute(uv, indexBuffer.array[i])
        const v1 = new THREE.Vector2().fromBufferAttribute(uv, indexBuffer.array[i + 1])
        const v2 = new THREE.Vector2().fromBufferAttribute(uv, indexBuffer.array[i + 2])

        const p0 = new THREE.Vector3((v0.x - minU) * uvToRaster, (v0.y - minV) * uvToRaster)
        const p1 = new THREE.Vector3((v1.x - minU) * uvToRaster, (v1.y - minV) * uvToRaster)
        const p2 = new THREE.Vector3((v2.x - minU) * uvToRaster, (v2.y - minV) * uvToRaster)

        const triangle = new THREE.Triangle(p0, p1, p2)

        const xMin = Math.floor(Math.min(p0.x, p1.x, p2.x))
        const xMax = Math.ceil(Math.max(p0.x, p1.x, p2.x))
        const yMin = Math.floor(Math.min(p0.y, p1.y, p2.y))
        const yMax = Math.ceil(Math.max(p0.y, p1.y, p2.y))

        for (let y = yMin; y <= yMax && y < resY; y++) {
            for (let x = xMin; x <= xMax && x < resX; x++) {
                const p = new THREE.Vector3(x + 0.5, y + 0.5)

                const baryCoords = new THREE.Vector3()
                if (triangle.getBarycoord(p, baryCoords) !== null) {
                    if (baryCoords.x > 0 && baryCoords.y > 0 && baryCoords.z > 0) {
                        const index = y * resX + x
                        const value = rasterizedAtlas[index]
                        if (value === 0) {
                            totalPixels++
                            rasterizedAtlas[index] = 1
                        } else if (value === 1) {
                            totalOverlappingPixels++
                            rasterizedAtlas[index] = 2
                        }
                    }
                }
            }
        }
    }

    if (totalPixels === 0) return 0
    return totalOverlappingPixels / totalPixels
}
